<template>
  <div>
    <div class="action-bar__summary">
      <div class="action-bar__summary-box action-bar__summary-box--product">
        <div class="action-bar__summary-product">
          <div class="action-bar__summary-product-image">
            <media
              v-if="mainProductImage"
              :media="mainProductImage"
              :cols="[3, 3, 6]"
              :lazyload="true"
            />
          </div>
          <div class="action-bar__summary-product-details">
            <h4 class="action-bar__summary-product-title">
              <span>{{
                decodeHtmlEntities(mainProduct?.decoratedProduct?.family ?? '')
              }}</span>
              <span>{{
                decodeHtmlEntities(
                  mainProduct?.decoratedProduct?.modelName ?? ''
                )
              }}</span>
            </h4>
            <div class="action-bar__summary-product-price-amount">
              <div class="action-bar__summary-product-meta">
                <amount-input
                  :model-value="mainProduct?.quantity"
                  readonly
                  :label="
                    $textDictionary['pdp.amount.label'] ?? 'pdp.amount.label'
                  "
                />
              </div>
              <div
                v-if="mainProduct?.decoratedProduct?.priceText"
                class="action-bar__summary-product-price"
              >
                <price-element
                  :current-price="mainProduct.decoratedProduct.priceText"
                  alignment="left"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-for="(item, index) in additionalProducts"
          :key="item.decoratedProduct?.sku ?? index"
          class="action-bar__summary-product"
        >
          <div class="action-bar__summary-product-image">
            <media
              v-if="getProductImage(item.decoratedProduct)"
              :media="getProductImage(item.decoratedProduct)"
              :cols="[3, 3, 6]"
              :lazyload="true"
            />
          </div>
          <div class="action-bar__summary-product-details">
            <h4 class="action-bar__summary-product-title">
              <span>{{
                decodeHtmlEntities(item.decoratedProduct?.modelName ?? '')
              }}</span>
            </h4>
            <SelectionPills
              v-if="
                enableCrossSell &&
                hasLengthVariants(item) &&
                !item.lengthEditOpen
              "
              class="action-bar-stack-length-pill"
              data-testid="action-bar-stack-length-change"
              :items="getLengthSelectionPills(item)"
              :aria-label="lengthSelectionAriaLabel"
              @action-click="openLengthEdit(item.decoratedProduct?.sku)"
            />
            <div
              v-if="
                enableCrossSell &&
                item.lengthEditOpen &&
                hasLengthVariants(item)
              "
              class="action-bar-stack-length-options"
              data-testid="action-bar-stack-length-options"
              role="group"
              :aria-label="lengthSelectionAriaLabel"
              :aria-busy="isLengthChangeLoading(item)"
            >
              <template v-if="isLengthChangeLoading(item)">
                <LoadingSkeleton
                  v-for="variant in getAlternativeLengthVariants(item)"
                  :key="variant.sku"
                  class="action-bar-stack-length-options__skeleton"
                />
              </template>
              <template v-else>
                <Button
                  v-for="variant in getAlternativeLengthVariants(item)"
                  :key="variant.sku"
                  type="button"
                  style-type="secondary"
                  size="small"
                  :disabled="crossSellBusy"
                  :title="getLengthVariantButtonLabel(variant)"
                  :aria-label="getLengthVariantButtonLabel(variant)"
                  @click="
                    onLengthSelect(item.decoratedProduct?.sku, variant.sku)
                  "
                >
                  <span>{{ getLengthVariantText(variant) }}</span>
                  <span
                    v-if="getLengthVariantPrice(variant)"
                    class="action-bar-stack-length-options__price"
                  >
                    {{ getLengthVariantPrice(variant) }}
                  </span>
                </Button>
              </template>
            </div>
            <div class="action-bar__summary-product-price-amount">
              <div class="action-bar__summary-product-meta">
                <amount-input
                  :model-value="item?.quantity"
                  readonly
                  :label="
                    $textDictionary['pdp.amount.label'] ?? 'pdp.amount.label'
                  "
                />
              </div>
              <div
                v-if="item?.decoratedProduct?.priceText"
                class="action-bar__summary-product-price"
              >
                <price-element
                  :current-price="item.decoratedProduct.priceText"
                  alignment="left"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="action-bar__summary-box action-bar__summary-box--cta">
        <div class="action-bar__summary-cart">
          <div
            v-if="cartStore.totalExcludingTax"
            class="action-bar__summary-cart-subtotal"
          >
            <span class="action-bar__summary-cart-subtotal-label">
              {{
                $textDictionary['cart.label.subtotal'] ?? 'cart.label.subtotal'
              }}
            </span>
            <span class="action-bar__summary-cart-subtotal-value">
              {{
                formatMoney(cartStore.totalExcludingTax, $activeLocale.webapp)
              }}
            </span>
          </div>
          <div class="action-bar__summary-cart-actions">
            <Button
              :style-type="
                displayGoToCheckoutButton ? 'secondary' : 'attraction'
              "
              size="medium"
              :expand="true"
              :disabled="crossSellBusy"
              @click="$emit('go-to-cart')"
            >
              {{
                $textDictionary['actionBar.shoppingCartButton'] ??
                'actionBar.shoppingCartButton'
              }}
            </Button>
            <Button
              v-if="displayGoToCheckoutButton"
              style-type="attraction"
              size="medium"
              :expand="true"
              :disabled="crossSellBusy"
              @click="$emit('go-to-checkout')"
            >
              {{ checkoutButtonText }}
            </Button>
          </div>
          <SecurePaymentInfo />
        </div>
      </div>
    </div>

    <ActionBarCrossSellSection
      v-if="enableCrossSell"
      :cards="crossSellCards"
      :product-name="productName"
      :loading="crossSellLoading"
      :busy="crossSellBusy"
      @add="(sku, groupId) => $emit('cross-sell-add', sku, groupId)"
      @update:selected-sku="
        (sku, groupId, position) =>
          $emit('cross-sell-length-change', sku, groupId, position)
      "
      @more-products-click="
        (remainingCount) => $emit('cross-sell-more', remainingCount)
      "
    />
  </div>
</template>

<script setup lang="ts">
import {
  Button,
  Media,
  PriceElement,
  AmountInput,
  SelectionPills,
  LoadingSkeleton,
  type SelectionPillItem
} from '@hypercodestudio/basler-components';
import SecurePaymentInfo from '../SecurePaymentInfo.vue';
import ActionBarCrossSellSection from './ActionBarCrossSellSection.vue';
import type { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { useCartStore } from '~/stores/cartStore/cartStore';
import { formatMoney } from '~/utils/shop/formatMoney';
import { CartTypes } from '~/stores/cartStore/model';
import useModernCheckout from '~/utils/shop/useModernCheckout';
import type { ActionBarCrossSellCardModel } from '~/utils/shop/buildActionBarCrossSellCards';
import type { ActionBarCrossSellVariant } from '~/utils/shop/selectActionBarCrossSellProduct';
import { sortActionBarCrossSellVariantsByLength } from '~/utils/shop/sortActionBarCrossSellVariantsByLength';
import { decodeHtmlEntities } from '~/utils/richtext/decodeHtmlEntities';

const { $textDictionary, $activeLocale, $globalPageSettings } = useNuxtApp();
const cartStore = useCartStore();

type CartItemInfo = {
  quantity: number;
  decoratedProduct: DecoratedProduct | any;
  config_id?: string;
  groupId?: string;
  lengthVariants?: ActionBarCrossSellVariant[];
  lengthEditOpen?: boolean;
};

interface Props {
  productSku: string;
  productName?: string;
  priceText: string;
  cartItems: readonly CartItemInfo[];
  crossSellCards?: ActionBarCrossSellCardModel[];
  crossSellLoading?: boolean;
  crossSellBusy?: boolean;
  enableCrossSell?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  productName: '',
  crossSellCards: () => [],
  crossSellLoading: false,
  crossSellBusy: false,
  enableCrossSell: false
});

const emit = defineEmits<{
  'go-to-cart': [];
  'go-to-checkout': [];
  'cross-sell-add': [sku: string, groupId: string];
  'cross-sell-length-change': [sku: string, groupId: string, position: number];
  'cross-sell-more': [remainingCount: number];
  'length-change': [currentSku: string, newSku: string];
}>();

const localCartItems = ref<CartItemInfo[]>([...props.cartItems]);
const lengthChangeLoadingSku = ref<string | null>(null);

watch(
  () => props.cartItems,
  (items) => {
    localCartItems.value = items.map((item) => ({ ...item }));
  },
  { deep: true }
);

watch(
  () => props.crossSellBusy,
  (busy) => {
    if (!busy) {
      lengthChangeLoadingSku.value = null;
    }
  }
);

const lengthLabelText = computed(
  () => $textDictionary.value['actionBar.crossSell.lengthLabel'] ?? 'Length'
);

const changeLengthText = computed(
  () => $textDictionary.value['actionBar.crossSell.changeLength'] ?? 'Change'
);

const lengthSelectionAriaLabel = computed(() => lengthLabelText.value);

const checkoutButtonText = computed(() => {
  return cartStore.activeCartType === CartTypes.CART
    ? cartStore.numberOfArticlesCart === 1
      ? ($textDictionary.value[
          'actionBar.checkoutButton.singleProduct'
        ]?.replace(
          '{numberOfArticles}',
          String(cartStore.numberOfArticlesCart)
        ) ?? 'actionBar.checkoutButton.singleProduct')
      : ($textDictionary.value[
          'actionBar.checkoutButton.multipleProducts'
        ]?.replace(
          '{numberOfArticles}',
          String(cartStore.numberOfArticlesCart)
        ) ?? 'actionBar.checkoutButton.multipleProducts')
    : cartStore.activeCartType === CartTypes.CUSTOMER_CART
      ? cartStore.numberOfArticlesCustomerCart === 1
        ? ($textDictionary.value[
            'actionBar.checkoutButton.singleProduct'
          ]?.replace(
            '{numberOfArticles}',
            String(cartStore.numberOfArticlesCustomerCart)
          ) ?? 'actionBar.checkoutButton.singleProduct')
        : ($textDictionary.value[
            'actionBar.checkoutButton.multipleProducts'
          ]?.replace(
            '{numberOfArticles}',
            String(cartStore.numberOfArticlesCustomerCart)
          ) ?? 'actionBar.checkoutButton.multipleProducts')
      : 'actionBar.checkoutButton.multipleProducts';
});

const mainProduct = computed(() => {
  const item = localCartItems.value.find((entry) => {
    return entry.decoratedProduct?.sku === props.productSku;
  });

  if (item) {
    return {
      decoratedProduct: item.decoratedProduct,
      quantity: item.quantity
    };
  }
  return null;
});

const additionalProducts = computed(() => {
  return localCartItems.value.filter((item) => {
    return item.decoratedProduct?.sku !== props.productSku;
  });
});

function getProductImage(decoratedProduct: DecoratedProduct | any) {
  if (!decoratedProduct) {
    return null;
  }

  return decoratedProduct.getProductImage?.(
    {
      sizes: '200px',
      widths: [400, 600, 800, 1000],
      aspectRatio: [4, 3]
    },
    $globalPageSettings.value?.shopConfig?.defaultProductImage ?? undefined
  );
}

const mainProductImage = computed(() => {
  return getProductImage(mainProduct.value?.decoratedProduct);
});

const displayGoToCheckoutButton = computed(() => useModernCheckout());

function hasLengthVariants(item: CartItemInfo): boolean {
  return (item.lengthVariants?.length ?? 0) > 1;
}

function getCurrentLengthLabel(item: CartItemInfo): string {
  const currentSku = item.decoratedProduct?.sku as string | undefined;
  const currentVariant = item.lengthVariants?.find(
    (variant: ActionBarCrossSellVariant) => variant.sku === currentSku
  );
  return decodeHtmlEntities(
    currentVariant?.lengthLabel || currentVariant?.name || currentSku || ''
  );
}

function getLengthSelectionPills(item: CartItemInfo): SelectionPillItem[] {
  const currentLength = getCurrentLengthLabel(item);
  return [
    {
      id: `action-bar-stack-length-${
        item.decoratedProduct?.sku ?? item.groupId
      }`,
      label: `${lengthLabelText.value}: ${currentLength}`,
      linkText: changeLengthText.value,
      isClickable: !props.crossSellBusy
    }
  ];
}

function getAlternativeLengthVariants(
  item: CartItemInfo
): ActionBarCrossSellVariant[] {
  const currentSku = item.decoratedProduct?.sku as string | undefined;
  return sortActionBarCrossSellVariantsByLength(
    (item.lengthVariants ?? []).filter(
      (variant: ActionBarCrossSellVariant) => variant.sku !== currentSku
    )
  );
}

function getLengthVariantText(variant: ActionBarCrossSellVariant): string {
  return decodeHtmlEntities(variant.lengthLabel || variant.name || variant.sku);
}

function getLengthVariantPrice(variant: ActionBarCrossSellVariant): string {
  if (variant.priceValue == null) {
    return '';
  }
  return formatMoney(
    { value: variant.priceValue, currency: variant.currency as never },
    $activeLocale.value.webapp
  );
}

function getLengthVariantButtonLabel(
  variant: ActionBarCrossSellVariant
): string {
  return [getLengthVariantText(variant), getLengthVariantPrice(variant)]
    .filter(Boolean)
    .join(' ');
}

function isLengthChangeLoading(item: CartItemInfo): boolean {
  const sku = item.decoratedProduct?.sku as string | undefined;
  return Boolean(sku && lengthChangeLoadingSku.value === sku);
}

function openLengthEdit(sku: string | undefined): void {
  if (!sku || props.crossSellBusy) {
    return;
  }
  localCartItems.value = localCartItems.value.map((item) => ({
    ...item,
    lengthEditOpen: item.decoratedProduct?.sku === sku
  }));
}

function onLengthSelect(
  currentSku: string | undefined,
  newSku: string | undefined
): void {
  if (
    !currentSku ||
    !newSku ||
    currentSku === newSku ||
    props.crossSellBusy ||
    lengthChangeLoadingSku.value
  ) {
    return;
  }
  lengthChangeLoadingSku.value = currentSku;
  emit('length-change', currentSku, newSku);
}
</script>

<style scoped>
.action-bar-stack-length-pill {
  margin: 0.25rem 0 0.5rem;
  padding-block-end: 0;
}

.action-bar-stack-length-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-block: 0.5rem;
}

.action-bar-stack-length-options__skeleton {
  width: 7rem;
  height: 2.25rem;
  border-radius: 16px;
}

.action-bar-stack-length-options__price {
  margin-inline-start: 0.75rem;
  font-weight: 400;
}

.action-bar__summary-product-title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
}
</style>
